import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
  useParams,
  useRouteError,
} from 'react-router';
import type { RouteObject } from 'react-router';
import type { ReactNode } from 'react';
import type { ProfileRow, SessionRow } from './data/db';
import {
  createProfile, deleteProfile, ensureDevice, exportProfile, importProfile, tombstoneDelivered,
  loadActiveProfile, loadDevice, loadProfiles, loadSessions, setActiveProfile, updateProfileLook,
} from './data/repository';
import type { Look } from './data/repository';
import type { Animal, Color } from './data/looks';
import { ALL_TILES, parseFactId } from './engine/facts';
import type { Fact } from './engine/facts';
import { masteryForAll } from './engine/mastery';
import { factStats } from './engine/stats';
import { report, отпустить } from './sentry';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { FactScreen } from './ui/FactScreen';
import { HistoryScreen } from './ui/HistoryScreen';
import { HomeScreen } from './ui/HomeScreen';
import { Hint, PrimaryButton, Screen, SecondaryButton, Title } from './ui/kit';
import { ProgressScreen } from './ui/ProgressScreen';
import { ResultsScreen } from './ui/ResultsScreen';
import { authConfigured, supabase } from './data/auth';
import {
  довестиУдаление, проверитьПередПовтором, проверитьУдаление, текстИсхода, удалитьУчётнуюЗапись,
} from './data/accountDeletion';
import { looksOffline } from './data/offline';
import { wipeDevice } from './data/wipe';
import { lookTakenOnServer, syncProfileList } from './data/sync';
import { AuthGate, AuthProvider, AuthUnconfigured, useAuth } from './ui/AuthGate';
import { ParentGate } from './ui/ParentGate';
import { ParentAnswerProvider, useParentAnswer } from './ui/ParentAnswer';
import { DeviceOwnerGate } from './ui/DeviceOwnerGate';
import { readLastShown, shouldRemind, writeLastShown } from './ui/localOnlyReminder';
import { PolicyScreen } from './ui/PolicyScreen';
import { TermsScreen } from './ui/TermsScreen';
import { SyncProvider, syncLine, useSync } from './ui/SyncContext';
import { LookPicker } from './ui/LookPicker';
import { ParentScreen, look as lookOf } from './ui/ParentScreen';
import { ProfilesProvider, useProfiles } from './ui/ProfilesContext';
import { ProfilesScreen } from './ui/ProfilesScreen';
import { downloadJson } from './ui/download';
import { текстОтказа } from './ui/errorText';
import { действие } from './ui/serverAction';
import { TrainerProvider, useTrainerContext } from './ui/TrainerContext';
import { TrainingScreen } from './ui/TrainingScreen';
import { useExitGuard } from './ui/useExitGuard';
import { resultsView } from './ui/resultsView';
import { exitGuardActive, trainingView } from './ui/trainingView';
import { useJournal } from './ui/useJournal';
import { tileFactIds } from './ui/progress';

const ALL_FACT_IDS = ALL_TILES.flatMap(tileFactIds);

function Loading() {
  return (
    <Screen>
      <Hint>Загрузка…</Hint>
    </Screen>
  );
}

/** Отказ хранилища. Показывается вместо вечного «Загрузка…»: IndexedDB
 *  отказывает в приватном окне, в webview внутри мессенджера и при откате
 *  выкладки на меньшую версию схемы, и без выхода ребёнок остаётся один на
 *  один со словом «Загрузка…». */
function LoadFailed({ what, onHome }: { what: string; onHome?: () => void }) {
  return (
    <Screen>
      <Title>Не удалось загрузить</Title>
      <Hint>
        {what} не читаются. Так бывает в режиме инкогнито и внутри некоторых
        приложений. Мы уже знаем об этой ошибке.
      </Hint>
      {/* На внутренних экранах одной перезагрузки мало: она возвращает на
          тот же адрес и упирается в тот же отказ. В установленном
          приложении системной кнопки «назад» нет, и ребёнок остаётся в
          петле без выхода. */}
      {onHome && (
        <SecondaryButton bottom onClick={onHome}>
          На главную
        </SecondaryButton>
      )}
      <PrimaryButton bottom={!onHome} onClick={() => window.location.reload()}>
        Попробовать снова
      </PrimaryButton>
    </Screen>
  );
}

/** Граница ошибок маршрутизатора.
 *
 *  Без неё react-router ставит собственную и перехватывает исключение
 *  раньше, чем оно дойдёт до `ErrorBoundary` из `main.tsx`: тот обёрнут
 *  СНАРУЖИ `RouterProvider` и не получает ничего. Ребёнок вместо русского
 *  «Что-то сломалось» видел белый экран с английским «Unexpected
 *  Application Error!» и полным стеком JavaScript — стек печатается и в
 *  боевой сборке, а карты кода выложены рядом. Sentry при этом не получал
 *  события, то есть обещание «мы уже знаем об этой ошибке» снова было
 *  неправдой, только в другом месте. */
function RouteError() {
  const error = useRouteError();

  useEffect(() => {
    report(error, 'route');
  }, [error]);

  return (
    <Screen>
      <Title>Что-то сломалось</Title>
      <Hint>Мы уже знаем об этой ошибке. Попробуй начать сначала.</Hint>
      <PrimaryButton bottom onClick={() => window.location.assign('/')}>
        На главную
      </PrimaryButton>
    </Screen>
  );
}

/** Обёртка, которая знает, чей это планшет.
 *
 *  Стоит НАД провайдером и задаёт ему `key`: переключение профиля обязано
 *  перемонтировать провайдер, иначе в памяти останутся курсоры раздачи
 *  предыдущего ребёнка и первая же сессия запишет их под ключ нового. Это
 *  второе состояние загрузки и отказа — над `ready` и `initFailed`, которые
 *  живут внутри провайдера и до его монтирования недоступны.
 *
 *  Профиль здесь больше не заводится сам. Ноль профилей — достижимое
 *  состояние (так выглядит устройство сразу после установки), и на него
 *  показывается экран создания, а не «синий ёж» по умолчанию: облик
 *  выбирает ребёнок, и выбранное им он не забудет. */
type GateState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'empty' }
  | { status: 'ready'; profiles: ProfileRow[]; active: ProfileRow };

async function readGate(): Promise<GateState> {
  await ensureDevice(() => crypto.randomUUID());
  /** Последовательно, а не `Promise.all`: чтение активного профиля — ещё и
   *  починка висячей ссылки, то есть транзакция на запись. Список,
   *  прочитанный параллельно с ней, мог бы отстать на одну правку. */
  const active = await loadActiveProfile();
  if (!active) return { status: 'empty' };
  return { status: 'ready', profiles: await loadProfiles(), active };
}

function ProfileGate({
  parentId, stale, children,
}: { parentId: string | null; stale: boolean; children: ReactNode }) {
  const [state, setState] = useState<GateState>({ status: 'loading' });
  const alive = useRef(true);
  /** Кого спрашивать о детях семьи: вошедшего родителя с живым токеном. Из
   *  ссылки, чтобы `refresh` не пересоздавался — его держат провайдер
   *  профилей и экран первого профиля. */
  const askServer = useRef<string | null>(null);
  askServer.current = parentId !== null && !stale ? parentId : null;
  const firstRead = useRef(true);

  const refresh = useCallback(async () => {
    try {
      let next = await readGate();
      /** Пустое устройство вошедшего родителя сначала спрашивает сервер.
       *
       *  Обмен живёт внутри готового шлюза и на пустом устройстве не
       *  начинается никогда. Второе устройство после входа показывало «Кто
       *  будет заниматься?» вместо детей семьи — при том что политика
       *  обещает: отправленное вернётся при следующем входе. А облик по
       *  умолчанию на всех устройствах один, и заведённый второй раз, он
       *  упирался в сервер уже после первых занятий.
       *
       *  Только при ПЕРВОМ чтении: пустота после удаления последнего
       *  ребёнка — не повод запускать второй обмен рядом с идущим. Ожидание
       *  ограничено внутри `syncProfileList`; без связи показывается
       *  создание, как прежде. */
      const first = firstRead.current;
      firstRead.current = false;
      const parent = askServer.current;
      if (first && next.status === 'empty' && parent !== null) {
        try {
          await syncProfileList(parent);
        } catch (error) {
          if (!looksOffline(error)) report(error, 'syncProfileList');
        }
        next = await readGate();
      }
      if (alive.current) setState(next);
    } catch (error) {
      report(error, 'ProfileGate');
      if (alive.current) setState({ status: 'failed' });
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    отпустить(refresh());
    return () => {
      alive.current = false;
    };
  }, [refresh]);

  if (state.status === 'failed') return <LoadFailed what="Сохранённые данные" />;
  if (state.status === 'loading') return <Loading />;
  if (state.status === 'empty') return <FirstProfile onDone={refresh} />;

  return (
    <ProfilesProvider value={{ profiles: state.profiles, active: state.active, refresh }}>
      <TrainerProvider key={state.active.id} profileId={state.active.id}>
        <SyncProvider parentId={parentId} stale={stale}>
          {children}
        </SyncProvider>
      </TrainerProvider>
    </ProfilesProvider>
  );
}

/** Отказ сервера в облике, названный в момент заведения или смены. Голос —
 *  как у подсказки выбора облика: выбирает ребёнок. */
const LOOK_TAKEN_ON_SERVER =
  'Такой цвет и зверёк уже заняты другим ребёнком. Выбери другой цвет или другого зверя.';

/** Свободен ли облик у сервера. Без учётной записи и с мёртвым токеном
 *  спрашивать некого; без ответа решает местная проверка. */
async function lookFree(signedIn: boolean, look: Look, exceptId: string | null): Promise<boolean> {
  if (!signedIn) return true;
  return (await lookTakenOnServer(look, exceptId)) !== true;
}

/** Первый профиль на устройстве. Вне маршрутизатора намеренно: до профиля
 *  внутренних экранов не существует вовсе, и адрес `/history` должен
 *  приводить сюда же, а не в пустую историю несуществующего ребёнка. */
function FirstProfile({ onDone }: { onDone: () => Promise<void> }) {
  const { session, stale } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <LookPicker
      title="Кто будет заниматься?"
      initial={{ color: 'blue', animal: 'hedgehog' }}
      taken={[]}
      submitLabel="Начать"
      busy={busy}
      error={error}
      onSubmit={(look) => {
        setBusy(true);
        setError(null);
        отпустить(lookFree(session !== null && !stale, look, null).then((free) => {
          if (!free) {
            setError(LOOK_TAKEN_ON_SERVER);
            setBusy(false);
            return;
          }
          return createProfile(look, () => crypto.randomUUID(), Date.now()).then(
            () => onDone().finally(() => setBusy(false)),
            (cause: unknown) => {
              report(cause, 'createProfile');
              setError('Не удалось завести профиль. Мы уже знаем об этой ошибке.');
              setBusy(false);
            },
          );
        }));
      }}
    />
  );
}

function Root() {
  const { ready, initFailed } = useTrainerContext();
  if (initFailed) return <LoadFailed what="Сохранённые данные" />;
  return ready ? <Outlet /> : <Loading />;
}

function HomeRoute() {
  const trainer = useTrainerContext();
  const navigate = useNavigate();
  const { active } = useProfiles();
  const { session, stale, signIn } = useAuth();
  const sync = useSync();
  /** Отложенный запуск тренировки: если перед началом надо напомнить про
   *  учётную запись, доводы запуска ждут здесь, пока взрослый не ответит.
   *  Складывать напоминание внутрь `trainer.start` нельзя — тренировка тогда
   *  началась бы под диалогом, и первая задача считалась бы показанной. */
  const [pendingStart, setPendingStart] = useState<Parameters<typeof trainer.start> | null>(null);

  const begin = (args: Parameters<typeof trainer.start>) => {
    trainer.start(...args);
    отпустить(navigate('/training'));
  };

  if (pendingStart !== null) {
    return (
      <ConfirmDialog
        title="Занятия только на этом устройстве"
        body={`Пройдено тренировок, которых нет больше нигде: ${sync.unsent}. Если браузер очистит хранилище или устройство потеряется, вернуть их будет нечем. Вход занимает минуту, и всё пройденное сохранится.`}
        cancelLabel="Потом"
        confirmLabel="Войти"
        onCancel={() => {
          const args = pendingStart;
          setPendingStart(null);
          begin(args);
        }}
        onConfirm={signIn}
      />
    );
  }

  return (
    <HomeScreen
      profile={active}
      onProfiles={() => отпустить(navigate('/profiles'))}
      /** Отказ обмена виден на главном экране, а не только в родительском
       *  разделе. Бесплатный тариф останавливает проект после недели без
       *  запросов, и тогда «всё хорошо» на экране означало бы занятия,
       *  которые никуда не уезжают, при полном молчании. */
      syncText={syncLine(sync.state, sync.unsent, sync.quarantined, stale, session !== null, sync.notSent)}
      onStart={(tiles, mode) => {
        const nowMs = Date.now();
        if (shouldRemind({
          signedIn: session !== null,
          unsent: sync.unsent,
          lastShownMs: readLastShown(),
          nowMs,
        })) {
          writeLastShown(nowMs);
          setPendingStart([tiles, mode]);
          return;
        }
        begin([tiles, mode]);
      }}
      onProgress={() => отпустить(navigate('/progress'))}
      onHistory={() => отпустить(navigate('/history'))}
    />
  );
}

function TrainingRoute() {
  const trainer = useTrainerContext();
  const navigate = useNavigate();
  const { queue, task, summary } = trainer;

  const view = trainingView(
    queue !== null,
    task !== null,
    trainer.finishFailed,
    summary !== null,
  );

  /** Перехват накрывает и дозапись, а не только саму тренировку. Окно между
   *  последним ответом и приходом сводки не мгновенно — там читается весь
   *  журнал попыток, — и уход в это окно лишал бы ребёнка итогов навсегда:
   *  показывать их было бы уже некому. */
  const guard = useExitGuard(exitGuardActive(view));

  // Сессия дописана в хранилище — уходим на итоги. `replace`, чтобы «назад»
  // не возвращало в завершённую тренировку.
  useEffect(() => {
    if (summary) отпустить(navigate(`/results/${summary.sessionId}`, { replace: true }));
  }, [summary, navigate]);

  const dialog = guard.blocked ? (
    <ConfirmDialog
      title="Выйти из тренировки?"
      body={
        view === 'finishing'
          ? 'Тренировка уже сохраняется, но итоги ты не увидишь: сколько примеров освоено и что стоит повторить.'
          : 'Эта тренировка не сохранится: ответы, которые уже даны, никуда не запишутся.'
      }
      cancelLabel={view === 'finishing' ? 'Подождать итоги' : 'Продолжить тренировку'}
      confirmLabel="Выйти и потерять"
      onCancel={() => {
        guard.cancel();
        // Часы ответа шли, пока диалог держал экран: начинаем отсчёт заново.
        trainer.resumeTimer();
      }}
      onConfirm={() => {
        // Сначала уходим, потом чистим: иначе очередь обнулится под
        // ещё смонтированным экраном и он мигнёт перенаправлением.
        guard.confirm();
        trainer.reset();
      }}
    />
  ) : null;

  if (view === 'gone') return <Navigate to="/" replace />;

  // Все задания решены, сессия дописывается в хранилище. Ждём сводку;
  // в `leaving` она уже пришла и эффект выше уводит на итоги.
  if (view === 'finishing' || view === 'leaving') {
    return (
      <>
        <Screen>
          <Hint>Сохраняем тренировку…</Hint>
        </Screen>
        {dialog}
      </>
    );
  }

  if (view === 'failed') {
    return (
      <Screen>
        <Title>Не удалось сохранить</Title>
        <Hint>
          Тренировка пройдена, но записать её не вышло. Мы уже знаем об этой ошибке.
        </Hint>
        <PrimaryButton
          bottom
          onClick={() => {
            trainer.reset();
            отпустить(navigate('/'));
          }}
        >
          На главную
        </PrimaryButton>
      </Screen>
    );
  }

  return (
    <>
      <TrainingScreen
        queue={queue!}
        task={task!}
        progress={trainer.progress}
        onAnswer={trainer.answer}
        onNext={trainer.next}
        onExit={() => отпустить(navigate('/'))}
        /** Автопереход замирает, пока висит диалог: иначе очередь уезжает
         *  под подложкой, и «Продолжить тренировку» возвращает ребёнка не
         *  туда, откуда он уходил. */
        paused={guard.blocked}
      />
      {dialog}
    </>
  );
}

function ResultsRoute() {
  const trainer = useTrainerContext();
  const navigate = useNavigate();
  const { sessionId } = useParams();

  const matching = trainer.summary !== null && trainer.summary.sessionId === sessionId;
  /** Итоги уже были на экране. Значит, исчезновение сводки — следствие
   *  нажатия на этом же экране, а не входа по устаревшей ссылке. */
  const everShown = useRef(false);
  if (matching) everShown.current = true;

  const view = resultsView(matching, trainer.training, everShown.current);
  if (view === 'restarted') return <Navigate to="/training" replace />;
  if (view === 'home') return <Navigate to="/" replace />;
  if (view === 'history') return <Navigate to="/history" replace />;

  return (
    <ResultsScreen
      summary={trainer.summary!}
      onRepeat={trainer.startRepeat}
      onFact={(factId) => отпустить(navigate(`/fact/${factId}`))}
      onDone={trainer.reset}
    />
  );
}

function ProgressRoute() {
  const navigate = useNavigate();
  const { profileId } = useTrainerContext();
  const journal = useJournal(profileId);
  if (journal.status === 'failed') {
    return <LoadFailed what="Записи о тренировках" onHome={() => отпустить(navigate('/'))} />;
  }
  if (journal.status === 'loading') return <Loading />;

  const states = masteryForAll(
    ALL_FACT_IDS,
    journal.journal.attempts,
    journal.journal.reveals,
  );
  return (
    <ProgressScreen
      states={states}
      onFact={(factId) => отпустить(navigate(`/fact/${factId}`))}
      onBack={() => отпустить(navigate('/'))}
    />
  );
}

function HistoryRoute() {
  const navigate = useNavigate();
  const { profileId } = useTrainerContext();
  const [sessions, setSessions] = useState<SessionRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (profileId === null) return;
    let alive = true;
    void loadSessions(profileId).then(
      (rows) => {
        if (alive) setSessions(rows);
      },
      (error: unknown) => {
        report(error, 'loadSessions');
        if (alive) setFailed(true);
      },
    );
    return () => {
      alive = false;
    };
  }, [profileId]);

  if (failed) return <LoadFailed what="Записи о тренировках" onHome={() => отпустить(navigate('/'))} />;
  if (!sessions) return <Loading />;
  return (
    <HistoryScreen
      sessions={sessions}
      onStart={() => отпустить(navigate('/'))}
      onBack={() => отпустить(navigate('/'))}
    />
  );
}

function FactRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const { factId = '' } = useParams();
  const { profileId } = useTrainerContext();
  const journal = useJournal(profileId);

  /** Сегмент маршрута — недоверенная строка: `/fact/d:7:2` даст дробное
   *  частное, а `/fact/m:99:5` — несуществующую плитку. Проверку делает
   *  `parseFactId`, здесь только показ отказа. */
  let fact: Fact;
  try {
    fact = parseFactId(factId);
  } catch {
    return (
      <Screen>
        <Title>Такого примера нет</Title>
        <Hint>Ссылка ведёт на пример, которого не существует в таблице.</Hint>
        <button
          type="button"
          onClick={() => отпустить(navigate('/progress'))}
          className="mt-auto rounded-[0.85rem] bg-accent p-4 text-[1.1rem] font-semibold text-white"
        >
          К успехам
        </button>
      </Screen>
    );
  }

  if (journal.status === 'failed') {
    return <LoadFailed what="Записи о тренировках" onHome={() => отпустить(navigate('/'))} />;
  }
  if (journal.status === 'loading') return <Loading />;

  const { attempts, reveals } = journal.journal;
  const state = masteryForAll([fact.id], attempts, reveals).get(fact.id)!;

  /** Ключ `default` означает, что это первая запись истории: ссылку открыли
   *  из закладки или приложение так стартовало. Возвращаться там некуда, и
   *  «назад» просто ничего бы не сделало. */
  const cameFromInside = location.key !== 'default';

  return (
    <FactScreen
      fact={fact}
      state={state}
      stats={factStats(fact.id, attempts)}
      onBack={() => отпустить(cameFromInside ? navigate(-1) : navigate('/progress'))}
    />
  );
}

/** Экран выбора ребёнка. Переключение перемонтирует провайдер через `key`
 *  в шлюзе, поэтому после `refresh` уходим на главную: остаться на месте
 *  значило бы показать список, под которым уже другой ребёнок. */
function ProfilesRoute() {
  const navigate = useNavigate();
  const { profiles, active, refresh } = useProfiles();
  return (
    <ProfilesScreen
      profiles={profiles}
      activeId={active.id}
      onPick={(profileId) => {
        if (profileId === active.id) {
          отпустить(navigate('/'));
          return;
        }
        void setActiveProfile(profileId).then(
          () => refresh().then(() => отпустить(navigate('/'))),
          (error: unknown) => report(error, 'setActiveProfile'),
        );
      }}
      onCreate={() => отпустить(navigate('/profiles/new'))}
      onParent={() => отпустить(navigate('/parent'))}
      onBack={() => отпустить(navigate('/'))}
    />
  );
}

/** Занятые сочетания для выбора облика. Считаются от живых профилей и без
 *  того, чей облик меняют: свой собственный зверь занятым не бывает. */
function takenLooks(profiles: ProfileRow[], exceptId?: string): Look[] {
  return profiles
    .filter((profile) => profile.id !== exceptId)
    .map((profile) => ({ color: profile.color as Color, animal: profile.animal as Animal }));
}

function NewProfileRoute() {
  const navigate = useNavigate();
  const { profiles, refresh } = useProfiles();
  const { session, stale } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <LookPicker
      title="Новый профиль"
      initial={{ color: 'green', animal: 'fox' }}
      taken={takenLooks(profiles)}
      submitLabel="Завести"
      busy={busy}
      error={error}
      onCancel={() => отпустить(navigate('/profiles'))}
      onSubmit={(look) => {
        setBusy(true);
        setError(null);
        отпустить(lookFree(session !== null && !stale, look, null).then((free) => {
          if (!free) {
            setError(LOOK_TAKEN_ON_SERVER);
            setBusy(false);
            return;
          }
          return createProfile(look, () => crypto.randomUUID(), Date.now()).then(
            () => refresh().then(() => отпустить(navigate('/profiles'))),
            (cause: unknown) => {
              report(cause, 'createProfile');
              setError(текстОтказа(cause, 'Не удалось завести профиль. Мы уже знаем об этой ошибке.'));
              setBusy(false);
            },
          );
        }));
      }}
    />
  );
}

function EditProfileRoute() {
  const navigate = useNavigate();
  const { profileId = '' } = useParams();
  const { profiles, refresh } = useProfiles();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { session, stale } = useAuth();
  const profile = profiles.find((row) => row.id === profileId);

  /** Профиль мог быть удалён в другой вкладке. Возврат в родительский
   *  раздел честнее пустого экрана. */
  if (!profile) return <Navigate to="/parent" replace />;

  return (
    <LookPicker
      title="Облик профиля"
      initial={{ color: profile.color as Color, animal: profile.animal as Animal }}
      taken={takenLooks(profiles, profileId)}
      submitLabel="Сохранить"
      busy={busy}
      error={error}
      onCancel={() => отпустить(navigate('/parent'))}
      onSubmit={(look) => {
        setBusy(true);
        setError(null);
        отпустить(lookFree(session !== null && !stale, look, profileId).then((free) => {
          if (!free) {
            setError(LOOK_TAKEN_ON_SERVER);
            setBusy(false);
            return;
          }
          return updateProfileLook(profileId, look).then(
            () => refresh().then(() => отпустить(navigate('/parent'))),
            (cause: unknown) => {
              report(cause, 'updateProfileLook');
              setError(текстОтказа(cause, 'Не удалось сохранить облик. Мы уже знаем об этой ошибке.'));
              setBusy(false);
            },
          );
        }));
      }}
    />
  );
}

/** Дверь родительского раздела — ОДНА на все его адреса: `/parent` и
 *  `/parent/:profileId` стоят под ней дочерними маршрутами.
 *
 *  Прежде вопрос задавал только `ParentRoute`, и прямой адрес правки облика
 *  открывался без него: чужую семью так не достать (профиль ищется в живых
 *  профилях родителя), но своего ребёнка переодеть можно было, минуя
 *  вопрос. После «Да» остаёмся на ЗАПРОШЕННОМ адресе — по `/parent/<id>`
 *  открывается правка облика, а не общий раздел. Сколько помнится ответ —
 *  см. `src/ui/ParentAnswer.tsx`. */
function ParentDoor() {
  const navigate = useNavigate();
  const { ответил, ответить } = useParentAnswer();
  if (!ответил) {
    return <ParentGate onPass={ответить} onBack={() => отпустить(navigate('/profiles'))} />;
  }
  return <Outlet />;
}

function ParentRoute() {
  const navigate = useNavigate();
  const { profiles, refresh } = useProfiles();
  const { session, stale, forget, signIn } = useAuth();
  const sync = useSync();
  const [showPolicy, setShowPolicy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [persisted, setPersisted] = useState<boolean | null>(null);

  useEffect(() => {
    отпустить(loadDevice().then((device) => setPersisted(device?.storagePersisted ?? null)), 'loadDevice');
  }, []);

  /** Шаги 8–9 удаления учётной записи: раздел открыт, а прошлое удаление
   *  кончилось неизвестно (`src/data/accountDeletion.ts`). Проверяется,
   *  когда раздел действительно показан, — после вопроса «А ты точно
   *  родитель?», а не за ним: запись удалена, значит устройство стирается и
   *  родителя выводит, и решать это за спиной вопроса незачем. Порядок
   *  держит `ParentDoor`: до ответа этот маршрут не монтируется вовсе.
   *
   *  Слова шага 3 обещают проверку именно здесь. Прежде её не было нигде,
   *  и родитель, удаливший запись при оборванном ответе, при повторном
   *  нажатии слышал «Неверная почта или пароль» — пароль проверяется у
   *  сервера, а пользователя там уже нет. Измерено исполнением. Проверка
   *  стоит на НОВОМ открытии раздела; повторное нажатие в том же окне
   *  закрыто карточкой 3в: `проверитьПередПовтором` спрашивает сервер до
   *  пароля (`src/data/accountDeletion.ts`). */
  const signedIn = session !== null;
  useEffect(() => {
    let alive = true;
    void проверитьУдаление(signedIn).then(
      (исход) => {
        /** Выход — даже если раздел успели закрыть: запись удалена,
         *  устройство стёрто, оставаться «вошедшим» не в кого. */
        if (исход === 'удалено') {
          forget();
          return;
        }
        if (!alive) return;
        const текст = текстИсхода(исход);
        if (текст === null) return;
        if (исход === 'данные-остались') {
          setNotice(null);
          setError(текст);
        } else {
          setNotice(текст);
        }
      },
      (сбой: unknown) => report(сбой, 'accountDeletion:раздел'),
    );
    return () => {
      alive = false;
    };
  }, [signedIn, forget]);

  if (showPolicy) return <PolicyScreen onBack={() => setShowPolicy(false)} />;
  if (showTerms) return <TermsScreen onBack={() => setShowTerms(false)} />;

  /** `null` — взрослый занимается без записи. Отличать от пустой строки
   *  обязательно: пустая означала бы «запись есть, адрес потерялся», а это
   *  другое положение дел и другие кнопки на экране. */
  const email = session === null ? null : session.user.email ?? '';

  /** Повторный ввод пароля проверяется у сервера, а не сравнением с чем-то
   *  местным: местного пароля у нас нет и быть не должно.
   *
   *  Проверка идёт через границу `действие` (`./ui/serverAction`), как и
   *  пять действий экрана входа. Прежде `signInWithPassword` звался мимо
   *  неё: занятость снималась в ветке `{ error }` и в `finally` вокруг
   *  самого действия, а бросок проверки — библиотека перебрасывает всё,
   *  что не `AuthError`, — вылетал раньше обоих. Кнопка подтверждения
   *  оставалась серой навсегда, объяснения не было. Измерено исполнением
   *  (`tests/ui/App.test.tsx`, «проверка пароля не оставляет раздел
   *  занятым»). */
  const withPassword = async (password: string, action: () => Promise<void>) => {
    /** Без учётной записи паролю неоткуда взяться, и проверять его не у
     *  кого. Сюда попасть нельзя: экран без записи не показывает ни одной
     *  кнопки, требующей пароля. Проверка стоит потому, что «не может
     *  случиться» уже дважды случалось, и молчаливое продолжение здесь
     *  означало бы удаление занятий без подтверждения. */
    if (email === null) throw new Error('пароль спрошен без учётной записи');
    setPasswordError(null);
    await действие({
      занять: setBusy,
      вызов: () => supabase().auth.signInWithPassword({ email, password }),
      отказ: setPasswordError,
      /** Отказ САМОГО действия разбирается здесь, а не границей: её слова
       *  для упавшего продолжения — «вход выполнен, но экран не открылся»,
       *  а здесь никто не входил, и действие не случилось. Граница при
       *  этом держит экран занятым до конца действия — выход и удаление
       *  долгие, и вторую кнопку не нажмут посреди первой. */
      успех: async () => {
        try {
          await action();
        } catch (failed) {
          report(failed, 'parentAction');
          /** Сетевой отказ отделяется от прочих: «попробуйте ещё раз» на
           *  оборванной связи отправляет родителя жать кнопку в пустоту, а
           *  причина не в нём. Именно так пряталась поломка CORS у серверной
           *  функции — на экране было общее «не получилось», и понять по нему
           *  было нечего. */
          /** Признак сети берётся из ОБЩЕГО списка (`looksOffline`). Здесь был
           *  четвёртый по счёту частный список из трёх подстрок, и нашёл его
           *  сторож, а не глаза: он знал про `failed to send`, но не знал ни
           *  про таймаут, ни про `Load failed` у Safari — то есть у половины
           *  родителей отказ связи выглядел бы как «не получилось, попробуйте
           *  ещё раз». `failed to send` добавлен в общий список. */
          setPasswordError(
            looksOffline(failed)
              ? 'Не удалось связаться с сервером. Проверьте интернет и попробуйте снова.'
              : 'Не получилось. Попробуйте ещё раз.',
          );
        }
      },
    });
  };

  const fail = (where: string, cause: unknown, text: string) => {
    report(cause, where);
    setNotice(null);
    setError(text);
  };

  return (
    <ParentScreen
      profiles={profiles}
      email={email}
      syncText={syncLine(sync.state, sync.unsent, sync.quarantined, stale, email !== null, sync.notSent)}
      unsent={sync.unsent}
      storagePersisted={persisted}
      notice={notice}
      error={error}
      passwordError={passwordError}
      /** Отказ прошлого окна не принадлежит следующему (карточка 3г). */
      onPasswordPrompt={() => setPasswordError(null)}
      busy={busy}
      onSignIn={signIn}
      onPolicy={() => setShowPolicy(true)}
      onTerms={() => setShowTerms(true)}
      onSyncNow={() => отпустить(sync.sync(), 'sync')}
      onReread={() => отпустить(sync.reread(), 'reread')}
      onRetrySend={() => отпустить(sync.sync(), 'sync')}
      onEdit={(profileId) => отпустить(navigate(`/parent/${profileId}`))}
      onExport={(profileId) => {
        const profile = profiles.find((row) => row.id === profileId);
        void exportProfile(profileId).then(
          (file) => {
            const stamp = new Date(file.exportedAt).toISOString().slice(0, 10);
            downloadJson(`mathpaw-${file.profile.color}-${file.profile.animal}-${stamp}.json`, file);
            setError(null);
            setNotice(`Занятия «${profile ? lookOf(profile) : profileId}» выгружены в файл.`);
          },
          (cause: unknown) => fail('exportProfile', cause, 'Не удалось выгрузить занятия.'),
        );
      }}
      onImport={(file) => {
        void file
          .text()
          .then((text) => importProfile(JSON.parse(text) as unknown))
          .then(
            (summary) => refresh().then(() => {
              setError(null);
              setNotice(`Загружено занятий: ${summary.sessions}, ответов: ${summary.attempts}.`);
              return sync.sync();
            }),
            (cause: unknown) =>
              fail('importProfile', cause, `Не удалось загрузить: ${текстОтказа(cause, 'файл не прочитался')}`),
          );
      }}
      onDelete={(profileId, password) => {
        /** Без записи подтверждение — обычный диалог, а не пароль: пароля
         *  не существует. Выше стоит только вопрос «А ты точно родитель?»,
         *  и это не защита — граница названа в `ParentGate`. */
        const run = async () => {
          await deleteProfile(profileId);
          await refresh();
          /** Надгробие надо донести до сервера сразу: пока оно не уехало,
           *  другое устройство продолжает показывать удалённого ребёнка. */
          await sync.sync();
          setError(null);
          /** Слова выбираются по тому, что СТАЛО, а не по тому, что мы
           *  сделали попытку. «Удалён навсегда» при неуехавшем надгробии —
           *  обещание, обратное правде: ребёнок вернётся из облака при
           *  следующем входе, а взрослый уже уверен, что его нет. */
          setNotice(
            (await tombstoneDelivered(profileId))
              ? 'Профиль удалён навсегда.'
              : 'Профиль удалён с этого устройства. Из облака он исчезнет, когда пройдёт синхронизация.',
          );
        };
        /** Без записи отказ разбирается ЗДЕСЬ: `withPassword`, который
         *  разбирает его во второй ветке, сюда не зовётся. Прежде стояло
         *  голое `void run()` — отказ базы уходил необработанным промисом,
         *  диалог к тому времени уже закрылся сам, и родитель не видел
         *  ничего: ребёнок в списке, объяснения нет. Измерено исполнением
         *  (`tests/ui/App.test.tsx`, «отказ удаления — объяснение, а не
         *  тишина»). Слова не утверждают, что не удалено: упасть может и
         *  шаг ПОСЛЕ удаления (пересчёт, чтение надгробия), а повтор
         *  безвреден — удаление надгробия ничего не делает. */
        if (password === null) {
          void run().catch((cause: unknown) =>
            fail('deleteProfile', cause, 'Удаление не завершилось. Попробуйте ещё раз.'));
        } else {
          отпустить(withPassword(password, run), 'withPassword:deleteProfile');
        }
      }}
      onSignOut={(password, everywhere) => {
        отпустить(withPassword(password, async () => {
          /** Область выхода задаётся ЯВНО обеими ветками.
           *
           *  `signOut()` без доводов у supabase-js — это `{ scope: 'global' }`
           *  (значение по умолчанию в сигнатуре), то есть прежнее
           *  `everywhere ? {scope:'global'} : undefined` выходило везде в
           *  обоих случаях. Две кнопки, которые экран честно различает
           *  словами, делали одно и то же: родитель, вышедший на планшете
           *  ребёнка, вылетал и со своего телефона. */
          await supabase().auth.signOut({ scope: everywhere ? 'global' : 'local' });
          await wipeDevice();
          forget();
        }), 'withPassword:signOut');
      }}
      onDeleteAccount={(password) => {
        /** Все исходы разбирает `удалитьУчётнуюЗапись` и возвращает
         *  значением — порядок, разбор ответа и пометку, переживающую
         *  перезагрузку, см. `src/data/accountDeletion.ts`. Бросать отсюда
         *  нельзя: `withPassword` показал бы «Не получилось», а запись к
         *  этому моменту, может быть, уже удалена. Слова — в окне пароля:
         *  оно остаётся на экране, и отказ в нём объявляется немедленно.
         *
         *  Карточки 3в и 3г: если прошлая попытка кончилась неизвестно или
         *  запись удалена, а стирание отказало, исход выясняется (или уже
         *  известен) ДО пароля (`проверитьПередПовтором`). Иначе второе
         *  нажатие в том же окне проверяло пароль под удалённой записью, и
         *  родитель слышал «Неверная почта или пароль.» о том, что
         *  получилось. Экран занят и на время этого вопроса: он сетевой, и
         *  второе нажатие посреди него ушло бы вторым ходом. */
        const удалить = async () => {
          const исход = await удалитьУчётнуюЗапись();
          if (исход === 'удалено') {
            forget();
            return;
          }
          setPasswordError(текстИсхода(исход));
        };
        setPasswordError(null);
        setBusy(true);
        void проверитьПередПовтором().then(
          (прежнее) => {
            if (прежнее === 'продолжать') {
              /** Занятость дальше держит граница `действие`: она занимает
               *  экран сразу, до первого ожидания, и отпускает в конце. */
              отпустить(withPassword(password, удалить), 'withPassword:deleteAccount');
              return;
            }
            setBusy(false);
            if (прежнее === 'удалено') {
              forget();
              return;
            }
            setPasswordError(текстИсхода(прежнее));
          },
          (сбой: unknown) => {
            /** Модуль не бросает по устройству; бросок — чужая поломка, и
             *  исход тогда неизвестен, а не «не получилось». */
            report(сбой, 'accountDeletion:повтор-нажатие');
            setBusy(false);
            setPasswordError(текстИсхода('неизвестно'));
          },
        );
      }}
      onBack={() => отпустить(navigate('/profiles'))}
    />
  );
}

/** Повтор уборки после удаления учётной записи — НАД шлюзом входа.
 *
 *  Шаг 7 таблицы (`src/data/accountDeletion.ts`): сервер подтвердил
 *  удаление, а стереть устройство тогда не вышло. Место выбрано по замеру,
 *  а не по вкусу: под шлюзом входа повтор не наступает никогда — после
 *  удаления записи сессии нет, открывается экран входа, и шлюз профилей не
 *  монтируется вовсе, а занятия удалённой семьи лежат в базе. Здесь же он
 *  идёт до всякой отрисовки приложения и от сессии не зависит.
 *
 *  Имя латиницей: правило хуков узнаёт компонент только по латинской
 *  заглавной (см. `AGENTS.md`, «Два слепых места линтера»). */
function FinishAccountDeletion({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'checking' | 'done' | 'failed'>('checking');

  useEffect(() => {
    let alive = true;
    void довестиУдаление().then(
      (итог) => {
        if (alive) setState(итог === 'не-стёрто' ? 'failed' : 'done');
      },
      (сбой: unknown) => {
        report(сбой, 'accountDeletion:запуск');
        if (alive) setState('failed');
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  if (state === 'checking') return <Loading />;
  /** Приложение НЕ отрисовывается: под ним занятия удалённой семьи, и
   *  показать их — ровно то, чего удаление обещало не допустить. */
  if (state === 'failed') {
    return (
      <Screen>
        <Title>Не удалось стереть данные удалённой учётной записи</Title>
        <Hint>{текстИсхода('не-стёрто')}</Hint>
        <PrimaryButton bottom onClick={() => window.location.reload()}>
          Попробовать снова
        </PrimaryButton>
      </Screen>
    );
  }
  return <>{children}</>;
}

/** Корень приложения: сначала учётная запись, потом ребёнок.
 *
 *  Порядок несущий. Профилей до учётной записи не существует вовсе
 *  (решение владельца: вход обязателен), поэтому шлюз профилей стоит ВНУТРИ
 *  шлюза входа, а не рядом с ним. */
function Shell() {
  /** Сборка без адреса сервера — это не «работает локально», а не работает
   *  вовсе. Экран об этом честнее белого. */
  if (!authConfigured()) return <AuthUnconfigured />;
  return (
    <FinishAccountDeletion>
      <AuthGate>
        {(auth) => (
          <AuthProvider value={auth}>
            {/* Ответ на вопрос перед родительским разделом — В КАЖДОЙ ветви,
                а не над шлюзом входа: выход снимает ветвь, и ответ умирает
                вместе с сеансом родителя. Довод — `src/ui/ParentAnswer.tsx`. */}
            {/* Без учётной записи заслон владельца не нужен: сверять не с чем,
                а данные без владельца достанутся тому, кто войдёт первым.
                Обмен при этом не запускается — `parentId` равен `null`, и
                `SyncProvider` про это знает. */}
            {auth.session === null ? (
              <ParentAnswerProvider>
                <ProfileGate parentId={null} stale={false}>
                  <Root />
                </ProfileGate>
              </ParentAnswerProvider>
            ) : (
              /* Сверка владельца стоит ВЫШЕ профилей и обмена: см. довод в
                 DeviceOwnerGate. Ключ по идентификатору родителя — чтобы при
                 смене записи заслон пересобрался, а не переиспользовал ответ,
                 полученный для прежней. */
              <DeviceOwnerGate
                key={auth.session.user.id}
                parentId={auth.session.user.id}
                email={auth.session.user.email ?? ''}
                onSignOut={() => отпустить(supabase().auth.signOut({ scope: 'local' }), 'DeviceOwnerGate:signOut')}
              >
                <ParentAnswerProvider>
                  <ProfileGate parentId={auth.session.user.id} stale={auth.stale}>
                    <Root />
                  </ProfileGate>
                </ParentAnswerProvider>
              </DeviceOwnerGate>
            )}
          </AuthProvider>
        )}
      </AuthGate>
    </FinishAccountDeletion>
  );
}

/** Таблица маршрутов вынесена из `App`, чтобы её можно было поднять в
 *  тестах на памяти. Два из трёх дефектов, найденных живым прогоном, были
 *  не в логике решения, а в проводке: с какими доводами маршрут зовёт эту
 *  логику и что делает с ответом. Без доступа к таблице этот слой не
 *  проверяется вовсе. */
export const routes: RouteObject[] = [
  {
    element: <Shell />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <HomeRoute /> },
      { path: '/training', element: <TrainingRoute /> },
      { path: '/results/:sessionId', element: <ResultsRoute /> },
      { path: '/progress', element: <ProgressRoute /> },
      { path: '/history', element: <HistoryRoute /> },
      { path: '/fact/:factId', element: <FactRoute /> },
      { path: '/profiles', element: <ProfilesRoute /> },
      { path: '/profiles/new', element: <NewProfileRoute /> },
      /** Одна дверь на весь раздел: адрес под `/parent`, заведённый
       *  дочерним маршрутом, спрашивает сам собой. */
      {
        path: '/parent',
        element: <ParentDoor />,
        children: [
          { index: true, element: <ParentRoute /> },
          { path: ':profileId', element: <EditProfileRoute /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

const router = createBrowserRouter(routes);

export function App() {
  return <RouterProvider router={router} />;
}
